import type { Dispatch } from 'redux';

import {
  TEALIUM_AD_TYPE,
  TEALIUM_CAJA_ABOUT_BUSINESS_CHARACTER_COUNT,
  TEALIUM_CAJA_CREATE_FLOW_TYPE,
  TEALIUM_CAJA_PAY_TYPE,
  TEALIUM_FINAL_JOB_SUMMARY_CHARACTER_COUNT,
  TEALIUM_HAS_BRAND_ASSETS,
  TEALIUM_PAGE,
  TEALIUM_PAYMENT_METHOD,
  TEALIUM_ROLE_TITLE,
  TEALIUM_SEEK_LISTING_ID,
  TEALIUM_TEXT_INPUT_LIST_UPDATE,
} from 'src/app/shared/constants/dispatcherId';
import {
  PAGE_ID_CLASSIFY,
  PAGE_ID_CONCIERGE,
  PAGE_ID_EDIT_PAY_AND_POST,
  PAGE_ID_EDIT,
  PAGE_ID_GUARANTEED_HIRE,
  PAGE_ID_MANAGE,
  PAGE_ID_PAY_AND_POST,
  PAGE_ID_REFRESH,
  PAGE_ID_SELECT_AD_TYPE,
  PAGE_ID_SUCCESS,
  PAGE_ID_WRITE,
  PAGE_ID_MULTI_LOCATION,
  PAGE_ID_PREMIUM_PLUS,
  PAGE_ID_EDIT_SUCCESS,
  PAGE_ID_REFRESH_SUCCESS,
  PAGE_ID_CREATE,
  PAGE_ID_REFRESH_ERROR,
} from 'src/app/shared/constants/pageUid';
import {
  CAJA_FLOW_TYPE_COPY,
  CAJA_FLOW_TYPE_CREATE,
  CAJA_FLOW_TYPE_DRAFT,
  REFERRER_COPY_JOB,
  REFERRER_CREATE_JOB,
  REFERRER_EDIT_DRAFT,
} from 'src/app/shared/constants/tealium';
import { log } from 'src/app/utils/logging';
import {
  TEALIUM_PAGE_CLASSIFY,
  TEALIUM_PAGE_CONCIERGE,
  TEALIUM_PAGE_EDIT_PAY_AND_POST,
  TEALIUM_PAGE_EDIT,
  TEALIUM_PAGE_GUARANTEED_HIRE,
  TEALIUM_PAGE_PAY_AND_POST,
  TEALIUM_PAGE_REFRESH,
  TEALIUM_PAGE_ROLE_REQS,
  TEALIUM_PAGE_SELECT_AD,
  TEALIUM_PAGE_SUCCESS,
  TEALIUM_PAGE_WRITE,
  TEALIUM_PAGE_MULTI_LOCATION,
  TEALIUM_PAGE_PREMIUM_PLUS,
  TEALIUM_PAGE_CREATE,
  TEALIUM_PAGE_REFRESH_ERROR,
} from 'src/app/v1/analytics';
import { actionUpdateCajaAboutBusinessCharacterCount } from 'src/app/v1/state/modules/tealium/cajaAboutBusinessCharacterCount';
import { actionUpdateCajaFlowType } from 'src/app/v1/state/modules/tealium/cajaFlowType';
import { actionUpdateCajaPayType } from 'src/app/v1/state/modules/tealium/cajaPayType';
import { actionUpdateFinalJobSummaryCharacterCount } from 'src/app/v1/state/modules/tealium/finalJobSummaryCharacterCount';
import { actionUpdateHasCoverImage } from 'src/app/v1/state/modules/tealium/hasCoverImage';
import { actionUpdateHasLogo } from 'src/app/v1/state/modules/tealium/hasLogo';
import { actionUpdateJobAdTypePosted } from 'src/app/v1/state/modules/tealium/jobAdTypePosted';
import { actionUpdateJobAdTypeSelected } from 'src/app/v1/state/modules/tealium/jobAdTypeSelected';
import { actionUpdateNumberOfBenefits } from 'src/app/v1/state/modules/tealium/numberOfBenefits';
import { actionUpdateNumberOfQualifications } from 'src/app/v1/state/modules/tealium/numberOfQualifications';
import { actionUpdateNumberOfTasks } from 'src/app/v1/state/modules/tealium/numberOfTasks';
import { actionUpdatePage } from 'src/app/v1/state/modules/tealium/page';
import { actionUpdatePaymentMethod } from 'src/app/v1/state/modules/tealium/paymentMethod';
import { actionUpdateRoleTitle } from 'src/app/v1/state/modules/tealium/roleTitle';
import { actionUpdateSeekListingId } from 'src/app/v1/state/modules/tealium/seekListingId';

import { getCharacterCount } from './utils';

export const tealiumPagePageIdMapping: Record<string, string> = {
  [PAGE_ID_CREATE]: TEALIUM_PAGE_CREATE,
  [PAGE_ID_CLASSIFY]: TEALIUM_PAGE_CLASSIFY,
  [PAGE_ID_SELECT_AD_TYPE]: TEALIUM_PAGE_SELECT_AD,
  [PAGE_ID_CONCIERGE]: TEALIUM_PAGE_CONCIERGE,
  [PAGE_ID_GUARANTEED_HIRE]: TEALIUM_PAGE_GUARANTEED_HIRE,
  [PAGE_ID_PREMIUM_PLUS]: TEALIUM_PAGE_PREMIUM_PLUS,
  [PAGE_ID_MULTI_LOCATION]: TEALIUM_PAGE_MULTI_LOCATION,
  [PAGE_ID_WRITE]: TEALIUM_PAGE_WRITE,
  [PAGE_ID_MANAGE]: TEALIUM_PAGE_ROLE_REQS,
  [PAGE_ID_PAY_AND_POST]: TEALIUM_PAGE_PAY_AND_POST,
  [PAGE_ID_SUCCESS]: TEALIUM_PAGE_SUCCESS,
  [PAGE_ID_EDIT_SUCCESS]: TEALIUM_PAGE_SUCCESS,
  [PAGE_ID_REFRESH_SUCCESS]: TEALIUM_PAGE_SUCCESS,
  [PAGE_ID_EDIT]: TEALIUM_PAGE_EDIT,
  [PAGE_ID_EDIT_PAY_AND_POST]: TEALIUM_PAGE_EDIT_PAY_AND_POST,
  [PAGE_ID_REFRESH]: TEALIUM_PAGE_REFRESH,
  [PAGE_ID_REFRESH_ERROR]: TEALIUM_PAGE_REFRESH_ERROR,
};

export default (dispatcherId: string, value: any) => (dispatch: Dispatch) => {
  if (value === undefined) {
    log.error('Tealium | Undefined value attempting to be dispatched', {
      dispatcherId,
    });
    return;
  }

  switch (dispatcherId) {
    case TEALIUM_AD_TYPE: {
      if (typeof value === 'string') {
        dispatch(actionUpdateJobAdTypePosted(value.toLowerCase()));
        dispatch(actionUpdateJobAdTypeSelected(value.toLowerCase()));
      }
      return null;
    }

    case TEALIUM_CAJA_ABOUT_BUSINESS_CHARACTER_COUNT: {
      const characterCount = getCharacterCount(value);
      return dispatch(
        actionUpdateCajaAboutBusinessCharacterCount(characterCount),
      );
    }

    case TEALIUM_CAJA_CREATE_FLOW_TYPE: {
      if (value === REFERRER_EDIT_DRAFT) {
        return dispatch(actionUpdateCajaFlowType(CAJA_FLOW_TYPE_DRAFT));
      }
      if (value === REFERRER_CREATE_JOB) {
        return dispatch(actionUpdateCajaFlowType(CAJA_FLOW_TYPE_CREATE));
      }
      if (value === REFERRER_COPY_JOB) {
        return dispatch(actionUpdateCajaFlowType(CAJA_FLOW_TYPE_COPY));
      }
      return null;
    }

    case TEALIUM_CAJA_PAY_TYPE: {
      return dispatch(actionUpdateCajaPayType(value));
    }

    case TEALIUM_FINAL_JOB_SUMMARY_CHARACTER_COUNT: {
      const characterCount = getCharacterCount(value);
      return dispatch(
        actionUpdateFinalJobSummaryCharacterCount(characterCount),
      );
    }

    case TEALIUM_HAS_BRAND_ASSETS: {
      dispatch(actionUpdateHasCoverImage(value.hasCoverImage));
      dispatch(actionUpdateHasLogo(value.hasLogo));
      return null;
    }

    case TEALIUM_PAGE: {
      const tealiumPage = tealiumPagePageIdMapping[value] || value;
      return dispatch(actionUpdatePage(tealiumPage));
    }

    case TEALIUM_PAYMENT_METHOD: {
      return dispatch(actionUpdatePaymentMethod(value));
    }

    case TEALIUM_ROLE_TITLE: {
      return dispatch(actionUpdateRoleTitle(value));
    }

    case TEALIUM_SEEK_LISTING_ID: {
      return dispatch(actionUpdateSeekListingId(value));
    }

    case TEALIUM_TEXT_INPUT_LIST_UPDATE: {
      switch (value.target) {
        case 'BENEFITS':
          return dispatch(actionUpdateNumberOfBenefits(value.count));
        case 'RESPONSIBILITIES':
          return dispatch(actionUpdateNumberOfTasks(value.count));
        case 'QUALIFICATIONS':
          return dispatch(actionUpdateNumberOfQualifications(value.count));
        default:
          return null;
      }
    }

    default: {
      return null;
    }
  }
};
